// Sürücü Puantajı — sürücü dinlenme analizi detay modalı.
// Çalışma blokları (≥4 saat ara olmadan birleşen servisler) ve bloklar arası nitelikli dinlenme.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import Modal from "shared/components/UIElements/Modal";
import Button from "shared/components/FormElements/Button";
import LoadingSpinner from "shared/components/UIElements/LoadingSpinner";

import useDriverPerformanceApi from "./hooks/useDriverPerformanceApi";
import { KIND_META, formatMinutes, formatNumber, longDate, shortDate } from "./utils";

import "./DriverPerformance.css";

const DriverRestDetailModal = ({ show, driver, range, onClose }) => {
  const { t } = useTranslation();
  const { isLoading, getRestDetail } = useDriverPerformanceApi();

  const [data, setData] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  const start = range?.start;
  const end = range?.end;

  const fetchDetail = useCallback(async () => {
    if (!driver?.id) return;
    setErrorMessage(null);
    try {
      const result = await getRestDetail(driver.id, { start, end });
      setData(result);
    } catch (err) {
      setErrorMessage(err?.message || t("driverPerformance.rest.fetchError"));
    }
  }, [driver, start, end, getRestDetail, t]);

  useEffect(() => {
    if (show && driver?.id) {
      fetchDetail();
    }
    if (!show) {
      setData(null);
      setErrorMessage(null);
    }
  }, [show, driver, fetchDetail]);

  const timeline = useMemo(() => {
    if (!data?.blocks?.length) return [];
    const items = [];
    data.blocks.forEach((block, i) => {
      items.push({ kind: "block", key: `block-${i}`, block });
      if (data.blockRests?.[i]) {
        items.push({ kind: "blockRest", key: `rest-${i}`, rest: data.blockRests[i] });
      }
    });
    return items;
  }, [data]);

  const summary = data?.summary;

  return (
    <Modal
      show={show}
      onCancel={onClose}
      onClose={onClose}
      className="dp-detail-modal dp-rest-modal"
      header={driver ? `${driver.name} — ${t("driverPerformance.rest.detailTitle")}` : t("driverPerformance.rest.detailTitle")}
      contentClass="dp-detail-modal__content"
      footerClass="dp-detail-modal__footer"
      footer={
        <Button type="button" inverse onClick={onClose}>
          {t("close")}
        </Button>
      }
    >
      {(start || end) && (
        <div className="dp-detail__range">
          {longDate(data?.start || start)} – {longDate(data?.end || end)}
        </div>
      )}

      {isLoading && !data && (
        <div className="dp-detail__loading">
          <LoadingSpinner />
          <span>{t("driverPerformance.loading")}</span>
        </div>
      )}

      {errorMessage && <div className="dp-detail__error">{errorMessage}</div>}

      {data && !errorMessage && (
        <>
          <p className="dp-rest__intro">{t("driverPerformance.rest.detailIntro")}</p>

          <div className="dp-rest__thresholds">
            <span className="dp-rest-threshold dp-rest-threshold--neutral">{t("driverPerformance.rest.thresholdSplit")}</span>
            <span className="dp-rest-threshold dp-rest-threshold--warning">{t("driverPerformance.rest.thresholdBlockWarn")}</span>
            <span className="dp-rest-threshold dp-rest-threshold--critical">{t("driverPerformance.rest.thresholdBlockCritical")}</span>
          </div>

          {summary && (
            <div className="dp-detail__summary dp-rest__summary">
              <SummaryCard label={t("driverPerformance.rest.summary.blocks")} value={formatNumber(summary.blockCount)} />
              <SummaryCard label={t("driverPerformance.rest.summary.violations")} value={formatNumber(summary.violationCount)} />
              <SummaryCard
                label={t("driverPerformance.rest.summary.longestBlock")}
                value={summary.longestBlockSpanMinutes > 0 ? formatMinutes(summary.longestBlockSpanMinutes) : "—"}
              />
              <SummaryCard
                label={t("driverPerformance.rest.summary.minQualifyingRest")}
                value={
                  summary.minQualifyingRestMinutes !== null && summary.minQualifyingRestMinutes !== undefined
                    ? formatMinutes(summary.minQualifyingRestMinutes)
                    : "—"
                }
              />
            </div>
          )}

          {data.violations?.length > 0 && (
            <div className="dp-rest__violations">
              <h4>
                {t("driverPerformance.rest.violationsTitle")} ({data.violations.length})
              </h4>
              <ul>
                {data.violations.map((v, i) => (
                  <li
                    key={`${v.type}-${v.startDate || v.afterDate}-${i}`}
                    className={`dp-rest-violation dp-rest-violation--${(v.severity || v.type).toLowerCase()}`}
                  >
                    <ViolationText violation={v} t={t} />
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="dp-rest__timeline">
            <h4>{t("driverPerformance.rest.timelineTitle")}</h4>
            {timeline.length === 0 && <div className="dp-detail__empty">{t("driverPerformance.rest.noTimedTrips")}</div>}
            {timeline.map((item) =>
              item.kind === "block" ? (
                <WorkBlock key={item.key} block={item.block} t={t} />
              ) : (
                <BlockRestGap key={item.key} rest={item.rest} t={t} />
              ),
            )}
          </div>
        </>
      )}

      {isLoading && data && <LoadingSpinner asOverlay />}
    </Modal>
  );
};

const SummaryCard = ({ label, value }) => (
  <div className="dp-summary-card">
    <span className="dp-summary-card__value">{value}</span>
    <span className="dp-summary-card__label">{label}</span>
  </div>
);

const ViolationText = ({ violation, t }) => {
  // Çakışan servis metni — OVERLAP uyarısı geçici kapalı (müşteri onayı bekleniyor)
  // if (violation.type === "OVERLAP") {
  //   return t("driverPerformance.rest.violation.overlap", {
  //     after: `${shortDate(violation.afterDate)} ${violation.afterEndTime}`,
  //     before: `${shortDate(violation.beforeDate)} ${violation.beforeStartTime}`,
  //   });
  // }
  const key =
    violation.severity === "critical"
      ? "driverPerformance.rest.violation.longBlockCritical"
      : "driverPerformance.rest.violation.longBlockWarning";
  return t(key, {
    span: formatMinutes(violation.spanMinutes),
    work: formatMinutes(violation.workMinutes),
    trips: violation.tripCount,
    start: `${shortDate(violation.startDate)} ${violation.startTime}`,
    end: `${shortDate(violation.endDate)} ${violation.endTime}`,
  });
};

const WorkBlock = ({ block, t }) => {
  const severityClass = block.severity === "critical" ? "critical" : block.severity === "warning" ? "warning" : "ok";
  return (
    <div className={`dp-rest-work-block dp-rest-work-block--${severityClass}`}>
      <div className="dp-rest-work-block__header">
        <span className="dp-rest-work-block__range">
          {shortDate(block.startDate)} {block.startTime} → {shortDate(block.endDate)} {block.endTime}
        </span>
        <span className="dp-rest-work-block__meta">
          {t("driverPerformance.rest.blockMeta", {
            trips: block.tripCount,
            span: formatMinutes(block.spanMinutes),
            work: formatMinutes(block.workMinutes),
          })}
        </span>
      </div>
      <div className="dp-rest-work-block__trips">
        {block.trips.map((trip, i) => (
          <TripRow key={`${trip.serviceId}-${i}`} trip={trip} t={t} />
        ))}
      </div>
    </div>
  );
};

const TripRow = ({ trip, t }) => {
  const meta = KIND_META[trip.kind];
  return (
    <div className="dp-rest-trip dp-rest-trip--nested">
      <span className="dp-rest-trip__kind" style={{ backgroundColor: meta?.bg || "#bbb", borderColor: meta?.border || "#999" }}>
        {t(meta?.labelKey || "driverPerformance.kind.STANDARD_PERSONEL")}
      </span>
      <span className="dp-rest-trip__time">
        {trip.startTime} → {trip.endTime}
      </span>
      <span className="dp-rest-trip__name">{trip.name}</span>
      {trip.workMinutes > 0 && <span className="dp-rest-trip__work">{formatMinutes(trip.workMinutes)}</span>}
    </div>
  );
};

const BlockRestGap = ({ rest, t }) => (
  <div className="dp-rest-gap dp-rest-gap--ok">
    <span className="dp-rest-gap__icon">↕</span>
    <span className="dp-rest-gap__label">{t("driverPerformance.rest.blockRestLabel", { value: formatMinutes(rest.restMinutes) })}</span>
    <span className="dp-rest-gap__detail">
      {shortDate(rest.afterDate)} {rest.afterEndTime} → {shortDate(rest.beforeDate)} {rest.beforeStartTime}
    </span>
  </div>
);

export default DriverRestDetailModal;
